import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { getConsent, saveConsent, restoreConsent } from '../utils/analytics/consent';
import { syncConsentToGtag } from '../utils/analytics/gtag';
import { syncConsentToSentry } from '../utils/analytics/sentry';
import { syncConsentToMeta } from '../utils/analytics/meta';
import { mirrorConsentToNative, readConsentFromNative } from '../utils/analytics/consentNativeMirror';
import { isNativeApp } from '../utils/platform';

// Single source of truth for cookie-consent state across the app.
//
//   const { analytics, marketing, personalization, openSettings, ... } = useConsent();
//
// State changes flow strictly one direction: a consumer calls one of
// the action methods (acceptAll, rejectOptional, savePreferences) →
// state updates in React → the vendor side-effect useEffects pick it
// up. No call site ever pokes window.gtag or Sentry directly; the load
// and revoke paths run from a single place so the lifecycle stays
// predictable.
const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  // Initial state hydrated from localStorage synchronously so the
  // banner-vs-no-banner decision happens on the first paint without a
  // flash. getConsent handles incognito / disabled-storage gracefully.
  const [consent, setConsent] = useState(() => getConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Push consent state into gtag whenever any axis changes. The
  // function is idempotent and reflects the full state every time —
  // no separate "load" vs "revoke" paths to keep in sync. Fires on
  // mount with the hydrated value, so a returning user with consent
  // saved gets the script loaded immediately.
  //
  // Gated on `decidedAt`: if the user hasn't made a decision yet
  // (banner visible, decidedAt === null), every axis is forced to
  // false even if the saved record has carry-over booleans from a
  // prior version. The version-migration path in getConsent()
  // pre-fills the toggles with old preferences for UX, but actually
  // pushing those into gtag/Sentry before the user re-confirms on the
  // current banner version would be a GDPR violation: the new
  // categories (e.g. Personalizare in v2) need explicit consent, and
  // an "analytics: true" inherited from v1 must NOT translate into
  // active tracking until the user clicks through the v2 banner.
  const decided = consent.decidedAt !== null;

  // Native: no first-launch consent banner (unlike the web, which needs
  // explicit opt-in for GA4/Meta/Sentry under GDPR Art. 6(1)(a) — the
  // near-universal pattern for consumer apps, e.g. Instagram/Uber, which
  // don't gate crash reporting behind a launch-time prompt either).
  // Crash/diagnostics (Sentry, native-only) runs on legitimate interest
  // (Art. 6(1)(f) — technical necessity of maintaining app stability and
  // security), data-minimised (no PII, no IP, no session replay — see
  // sentry.js). There is NO in-app toggle (matches Mero/Fresha); the
  // Art. 21 opt-out route is a request to privacy@petzy.ro (/gdpr page).
  //
  // Two things can happen on a fresh native launch with no LOCAL record:
  //   1. A prior decision survives in @capacitor/preferences (the user
  //      explicitly toggled diagnostics before, WebView storage was later
  //      evicted) → restore THAT decision, on or off, never re-prompt.
  //   2. Truly first-ever launch, nothing anywhere → auto-grant analytics
  //      (Sentry) silently, no UI. This is the ONLY difference from the
  //      restore-only web-equivalent flow.
  // The freshness re-check before each write closes the same race the web
  // banner has to handle: a decision made in Setări while this async read
  // is in flight must always win over the mirror/auto-grant.
  useEffect(() => {
    if (!isNativeApp || decided) return;
    let cancelled = false;
    readConsentFromNative().then((record) => {
      if (cancelled) return;
      if (getConsent().decidedAt !== null) return;
      const restored = restoreConsent(record);
      if (restored) {
        setConsent((current) => (current.decidedAt === null ? restored : current));
        return;
      }
      if (getConsent().decidedAt !== null) return;
      const granted = saveConsent({ analytics: true, marketing: false, personalization: false });
      setConsent((current) => (current.decidedAt === null ? granted : current));
    });
    return () => { cancelled = true; };
  }, [decided]);

  // Mirror every decided consent state to native storage so it survives
  // WebView cache eviction. Fire-and-forget; no-op on web.
  useEffect(() => {
    if (decided) mirrorConsentToNative(consent);
  }, [decided, consent]);

  useEffect(() => {
    syncConsentToGtag({
      analytics: decided && consent.analytics,
      marketing: decided && consent.marketing,
      personalization: decided && consent.personalization,
    });
  }, [decided, consent.analytics, consent.marketing, consent.personalization]);

  // Sentry is gated on the Analytics category. WEB: the SDK collects
  // session replays + performance traces (analytical measurement) —
  // Sentry.init requires the same explicit banner decision as gtag,
  // never inherits from a stale pre-version record. NATIVE: this axis
  // instead gates crash/diagnostics only (no replay/tracing there — see
  // sentry.js), auto-granted above rather than banner-decided; `decided`
  // still gates it so the auto-grant effect's write always lands before
  // Sentry reacts to it.
  useEffect(() => {
    syncConsentToSentry({ analytics: decided && consent.analytics });
  }, [decided, consent.analytics]);

  // Meta Pixel is gated on the Marketing category — same axis the
  // inline bootstrap in index.html uses for the first-paint script-
  // load decision. This effect handles the in-session flip: visitor
  // who refused on the banner sees `fbq.queue` populated (init +
  // PageView pushed by the inline bootstrap regardless of consent,
  // since the queue is in-memory only and doesn't fire requests) but
  // no fbevents.js on the page; when they flip marketing on via the
  // settings modal, syncConsentToMeta injects the script, queue
  // drains, both calls reach Meta. Revoke calls fbq('consent',
  // 'revoke') AND clears _fbp/_fbc cookies so Cookiebot scans don't
  // flag stale tracker cookies post-refusal. Same `decided` gate as
  // the other two vendors.
  useEffect(() => {
    syncConsentToMeta({ marketing: decided && consent.marketing });
  }, [decided, consent.marketing]);

  const acceptAll = useCallback(() => {
    setConsent(saveConsent({ analytics: true, marketing: true, personalization: true }));
    setSettingsOpen(false);
  }, []);

  // "Refuz total" path — explicitly recording the decision (decidedAt
  // gets stamped) so the banner doesn't reappear on next visit just
  // because every category is false.
  const rejectOptional = useCallback(() => {
    setConsent(saveConsent({ analytics: false, marketing: false, personalization: false }));
    setSettingsOpen(false);
  }, []);

  const savePreferences = useCallback(({ analytics, marketing, personalization }) => {
    setConsent(saveConsent({ analytics, marketing, personalization }));
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(() => ({
    necessary: consent.necessary,
    analytics: consent.analytics,
    marketing: consent.marketing,
    personalization: consent.personalization,
    decidedAt: consent.decidedAt,
    bannerVisible: consent.decidedAt === null,
    settingsOpen,
    acceptAll,
    rejectOptional,
    savePreferences,
    openSettings,
    closeSettings,
  }), [consent, settingsOpen, acceptAll, rejectOptional, savePreferences, openSettings, closeSettings]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    // Calling useConsent outside the provider almost always means the
    // call site forgot to wrap App in ConsentProvider. Throw loud rather
    // than silently returning a no-op object.
    throw new Error('useConsent must be used inside <ConsentProvider>');
  }
  return ctx;
}
